import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Fancybox } from '@fancyapps/ui';
import { BaseComponentImpl } from '../../types/implementations';
import { Building, PhotoModel } from '../../types/protocols';
import { useFirebaseImage } from '../../Helpers/ImageLoader';
import { useUsersData } from '../../Helpers/UsersContext';
import { buildPhotoCaption } from '../../Helpers/photoCaptionUtils';
import ComponentDetailsView from '../ComponentPage/ComponentDetailsView';
import './RoomComponentDetailsPane.css';

export type RightPaneContent =
    | { type: 'component'; component: BaseComponentImpl }
    /** `allPhotos` in the order the gallery shows them, which the preview navigates in. */
    | { type: 'photo'; photo: PhotoModel; url: string; allPhotos: PhotoModel[]; buildingKey: string; title?: string }
    | { type: 'document'; url: string; fileName: string; mimeType: string }
    | null;

interface RoomComponentDetailsPaneProps {
    content: RightPaneContent;
    building: Building;
    buildingKey: string;
    hasComponents: boolean;
}

/** Sub-component to load a single photo URL for FancyBox gallery */
const PhotoUrlLoader: React.FC<{
    storagePath: string;
    index: number;
    onUrlLoaded: (index: number, url: string) => void;
}> = ({ storagePath, index, onUrlLoaded }) => {
    const { src, isLoading } = useFirebaseImage(storagePath);
    const onUrlLoadedRef = useRef(onUrlLoaded);
    onUrlLoadedRef.current = onUrlLoaded;

    useEffect(() => {
        if (!isLoading && src) {
            onUrlLoadedRef.current(index, src);
        }
    }, [src, isLoading, index]);

    return null;
};

const PhotoPreview: React.FC<{
    photo: PhotoModel;
    url: string;
    allPhotos: PhotoModel[];
    title?: string;
}> = ({ photo, url, allPhotos, title }) => {
    const photoEntries = allPhotos;
    const [loadedUrls, setLoadedUrls] = useState<{ [index: number]: string }>({});
    const { users } = useUsersData();

    const handleUrlLoaded = useCallback((index: number, loadedUrl: string) => {
        setLoadedUrls(prev => ({ ...prev, [index]: loadedUrl }));
    }, []);

    const handleClick = () => {
        const gallery = photoEntries.map((p, i) => ({
            src: loadedUrls[i] || p.storagePath,
            caption: buildPhotoCaption(p, users),
        }));

        const startIndex = photoEntries.findIndex(p => p.storagePath === photo.storagePath);

        Fancybox.show(gallery, {
            animated: true,
            showClass: 'fancybox-fadeIn',
            startIndex: startIndex >= 0 ? startIndex : 0,
        });
    };

    return (
        <div style={{ position: 'sticky', top: '20px' }}>
            <div className="std-box dropshadow" style={{ padding: '20px' }}>
                <h5 style={{ marginBottom: '12px' }}>{title || 'Foto'}</h5>
                {photo.date && (
                    <small style={{ color: '#888', display: 'block', marginBottom: '12px' }}>{photo.date}</small>
                )}
                <img
                    src={url}
                    alt={title || 'Foto'}
                    style={{
                        width: '100%',
                        borderRadius: '8px',
                        cursor: 'pointer',
                    }}
                    onClick={handleClick}
                    title="Klicka för att öppna bildvisare"
                />
            </div>
            {/* Hidden loaders for all photos to populate FancyBox gallery */}
            {photoEntries.map((p, i) => (
                <PhotoUrlLoader
                    key={p.storagePath}
                    storagePath={p.storagePath}
                    index={i}
                    onUrlLoaded={handleUrlLoaded}
                />
            ))}
        </div>
    );
};

const DocumentPreview: React.FC<{
    url: string;
    fileName: string;
    mimeType: string;
}> = ({ url, fileName, mimeType }) => {
    const isImage = mimeType.startsWith('image/');

    return (
        <div style={{ position: 'sticky', top: '20px' }}>
            <div className="std-box dropshadow" style={{ padding: '20px' }}>
                <h5 style={{ marginBottom: '12px' }}>{fileName}</h5>
                {isImage ? (
                    <img
                        src={url}
                        alt={fileName}
                        style={{ width: '100%', borderRadius: '8px' }}
                    />
                ) : (
                    <iframe
                        src={url}
                        style={{ width: '100%', height: '600px', border: 'none', borderRadius: '8px' }}
                        title={fileName}
                    />
                )}
            </div>
        </div>
    );
};

const RoomComponentDetailsPane: React.FC<RoomComponentDetailsPaneProps> = ({
    content,
    building,
    buildingKey,
    hasComponents,
}) => {
    if (content?.type === 'component') {
        return (
            <ComponentDetailsView
                component={content.component}
                building={building}
                buildingKey={buildingKey}
                showHeader={true}
                headerActions={
                    <Link
                        className="open-tab-button"
                        to={`/building/${buildingKey}/component/${content.component.key}`}
                        target="_blank"
                        title="Öppna i ny flik"
                    >
                        ↗
                    </Link>
                }
                containerStyle={{
                    position: 'sticky',
                    top: '20px'
                }}
            />
        );
    }

    if (content?.type === 'photo') {
        return (
            <PhotoPreview
                photo={content.photo}
                url={content.url}
                allPhotos={content.allPhotos}
                title={content.title}
            />
        );
    }

    if (content?.type === 'document') {
        return (
            <DocumentPreview
                url={content.url}
                fileName={content.fileName}
                mimeType={content.mimeType}
            />
        );
    }

    if (!hasComponents) return null;

    return (
        <div
            style={{
                width: '100%',
                minHeight: '400px',
                border: '2px dashed #ccc',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafa',
                position: 'sticky',
                top: '20px'
            }}
        >
            <p
                style={{
                    color: '#666',
                    fontSize: '16px',
                    textAlign: 'center',
                    margin: 0,
                    padding: '20px'
                }}
            >
                Klicka på en komponent för att visa mer info här
            </p>
        </div>
    );
};

export default RoomComponentDetailsPane;
